import React from 'react';
import { useTranslation } from 'react-i18next';
import { styled, keyframes } from '@mui/material/styles';
import { Typography, Button, Box } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { images, getImageUrl } from '../utils/imageUtils';

const floatBoat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-18px); }
`;

const StyledSection = styled('section')(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  width: '100%',
  background: 'linear-gradient(120deg, #fcfdff 0%, #f7fafd 100%)',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2, 0),
  [theme.breakpoints.down('sm')]: {
    minHeight: '100vh',
    padding: theme.spacing(0, 0),
  },
}));

const LightOverlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(255,255,255,0.38)',
  backdropFilter: 'blur(2.5px)',
  zIndex: 1,
}));

const BoatImage = styled('img')(({ theme }) => ({
  position: 'absolute',
  left: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: 2,
  opacity: 0.38,
  filter: 'none',
  pointerEvents: 'none',
  userSelect: 'none',
}));

const StyledContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 3,
  maxWidth: '820px',
  width: '100%',
  margin: '0 auto',
  textAlign: 'center',
  color: theme.palette.text.primary,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4, 2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 0.5),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.getContrastText(theme.palette.primary.main),
  fontWeight: 700,
  fontSize: '1.15rem',
  borderRadius: 18,
  padding: theme.spacing(1.7, 5),
  boxShadow: '0 4px 20px rgba(44,76,124,0.10)',
  transition: 'all 0.3s',
  marginTop: theme.spacing(4),
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.getContrastText(theme.palette.primary.dark),
    transform: 'scale(1.07)',
    boxShadow: '0 8px 32px rgba(44,76,124,0.18)',
  },
}));

const Hero = () => {
  const { t } = useTranslation();

  return (
    <StyledSection id="home">
      <LightOverlay />
      <BoatImage 
        src={getImageUrl(images.cargoContainers)}
        alt="Cargo Containers"
        onError={(e) => {
          console.error('Error loading image:', e);
          e.target.onerror = null;
          e.target.src = getImageUrl(images.cargoContainers);
        }}
      />
      <StyledContent>
        <Typography
          variant="h1"
          component="h1"
          gutterBottom
          sx={{
            color: '#223a5e',
            fontWeight: 700,
            fontSize: { xs: '2.1rem', sm: '2.7rem', md: '3.2rem' },
            letterSpacing: '-0.5px',
            mb: 2.5,
            textShadow: 'none',
          }}
        >
          {t('hero.title')}
        </Typography>
        <Typography
          variant="h5"
          component="p"
          paragraph
          sx={{
            color: '#3a4a6b',
            fontWeight: 400,
            fontSize: { xs: '1.05rem', sm: '1.18rem', md: '1.22rem' },
            mb: 4,
            textShadow: 'none',
          }}
        >
          {t('hero.subtitle')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'center', gap: 3, mt: 2, width: '100%' }}>
          <StyledButton
            variant="contained"
            size="large"
            href="#quote"
            sx={{
              backgroundColor: '#c9a74c',
              color: '#1a2340',
              fontWeight: 700,
              boxShadow: '0 6px 24px rgba(201,167,76,0.18)',
              width: { xs: '100%', sm: 'auto' },
              maxWidth: 320,
              '&:hover': {
                backgroundColor: '#b8933d',
                color: '#fff',
                boxShadow: '0 8px 32px rgba(201,167,76,0.22)',
              },
            }}
          >
            {t('hero.cta')}
          </StyledButton>
        </Box>
      </StyledContent>
    </StyledSection>
  );
};

export default Hero; 